import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import JobSearchStartDateModal from '../components/JobSearchStartDateModal';
import RescanConfirmModal from '../components/RescanConfirmModal';
import api from '../backend/api';
import './settings.css';

const Settings = () => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userInfo, setUserInfo] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [showRescanModal, setShowRescanModal] = useState(false);
  const [gmailSyncStatus, setGmailSyncStatus] = useState({
    auto_sync_enabled: false,
    last_sync: null,
    sync_frequency: 30,
    has_credentials: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    // Fetch fresh user info from API to get picture
    fetchCurrentUser();
    // Fetch Gmail sync status and profile
    fetchGmailSyncStatus();
    fetchProfile();
  }, [user, navigate]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me/');
      setUserInfo(response.data);
      // Also update localStorage with fresh data including picture
      const currentUser = JSON.parse(localStorage.getItem('user_data') || '{}');
      localStorage.setItem('user_data', JSON.stringify({...currentUser, ...response.data}));
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      // Fallback to stored user object
      if (user?.user) {
        setUserInfo(user.user);
      }
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile/');
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const handleSaveStartDate = async (date) => {
    try {
      const response = await api.put('/profile/', {
        job_search_start_date: date
      });
      setProfile(response.data);
      
      if (response.data.rescan_suggested) {
        setShowRescanModal(true);
      }
    } catch (error) {
      console.error('Error saving start date:', error);
      throw error;
    }
  };

  const handleRescanConfirm = () => {
    setShowRescanModal(false);
    navigate('/', { state: { triggerScan: true } });
  };

  const calculateDaysScanned = () => {
    if (!profile?.job_search_start_date) return 90;
    
    const startDate = new Date(profile.job_search_start_date);
    const today = new Date();
    return Math.floor((today - startDate) / (1000 * 60 * 60 * 24));
  };

  const fetchGmailSyncStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gmail/auto-sync/status/`, {
        headers: {
          'Authorization': `Bearer ${user?.accessToken || localStorage.getItem('access_token')}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGmailSyncStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch Gmail sync status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGmailSyncToggle = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gmail/auto-sync/toggle/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user?.accessToken || localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: !gmailSyncStatus.auto_sync_enabled,
          sync_frequency: gmailSyncStatus.sync_frequency
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGmailSyncStatus(prev => ({
          ...prev,
          auto_sync_enabled: data.auto_sync_enabled,
          sync_frequency: data.sync_frequency
        }));
      }
    } catch (error) {
      console.error('Failed to toggle Gmail sync:', error);
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail? This will delete your stored credentials.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/gmail/disconnect/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user?.accessToken || localStorage.getItem('access_token')}`,
        }
      });

      if (response.ok) {
        setGmailSyncStatus({
          auto_sync_enabled: false,
          last_sync: null,
          sync_frequency: 30,
          has_credentials: false
        });
        alert('Gmail disconnected successfully');
      }
    } catch (error) {
      console.error('Failed to disconnect Gmail:', error);
      alert('Failed to disconnect Gmail');
    }
  };

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="profile-header-content">
          <img 
            src={userInfo?.picture || 'https://via.placeholder.com/80x80/039772/ffffff?text=' + (userInfo?.first_name?.charAt(0) || 'U')} 
            alt="Profile" 
            className="profile-picture-large"
          />
          <div className="profile-header-info">
            <h1>{userInfo?.first_name} {userInfo?.last_name}</h1>
            <p className="profile-email">{userInfo?.email}</p>
          </div>
        </div>
      </div>

      <div className="profile-content">
        <div className="profile-sidebar">
          <nav className="profile-nav">
            <Link 
              to="/profile" 
              className={location.pathname === '/profile' ? 'profile-nav-link active' : 'profile-nav-link'}
            >
              Profile Information
            </Link>
            <Link 
              to="/profile/settings" 
              className={location.pathname === '/profile/settings' ? 'profile-nav-link active' : 'profile-nav-link'}
            >
              Settings
            </Link>
          </nav>
        </div>

        <div className="profile-main">
          <div className="settings-header">
            <h1>Settings</h1>
            <p className="settings-subtitle">Manage your account settings and preferences</p>
          </div>

      <div className="settings-sections">
        {/* Job Search Settings */}
        <div className="settings-section">
          <h2>Job Search Settings</h2>
          <div className="settings-card">
            <div className="settings-item">
              <div className="settings-item-info">
                <strong>Job Search Start Date</strong>
                <p>Determines how far back to scan your emails for job applications</p>
                {profile?.job_search_start_date ? (
                  <p className="setting-value">
                    📅 {new Date(profile.job_search_start_date).toLocaleDateString()} 
                    <span className="info-badge"> ({calculateDaysScanned()} days)</span>
                  </p>
                ) : (
                  <p className="warning-text">⚠️ Not set - using default (90 days)</p>
                )}
              </div>
              <div className="settings-item-action">
                <button 
                  className="update-button"
                  onClick={() => setShowStartDateModal(true)}
                >
                  {profile?.job_search_start_date ? 'Update Date' : 'Set Date'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Gmail Integration Settings */}
        <div className="settings-section">
          <h2>Gmail Integration</h2>
          <div className="settings-card">
            <div className="settings-item">
              <div className="settings-item-info">
                <strong>Gmail Status</strong>
                <p>
                  {gmailSyncStatus.has_credentials 
                    ? '✓ Connected' 
                    : '✗ Not connected'}
                </p>
              </div>
            </div>

            {gmailSyncStatus.has_credentials && (
              <>
                <div className="settings-item">
                  <div className="settings-item-info">
                    <strong>Auto-Sync</strong>
                    <p>Automatically sync job application emails</p>
                  </div>
                  <div className="settings-item-action">
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={gmailSyncStatus.auto_sync_enabled}
                        onChange={handleGmailSyncToggle}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                {gmailSyncStatus.last_sync && (
                  <div className="settings-item">
                    <div className="settings-item-info">
                      <strong>Last Sync</strong>
                      <p>{new Date(gmailSyncStatus.last_sync).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                <div className="settings-item">
                  <div className="settings-item-info">
                    <strong>Sync Frequency</strong>
                    <p>Every {gmailSyncStatus.sync_frequency} minutes</p>
                  </div>
                </div>

                <div className="settings-item">
                  <button 
                    className="disconnect-button"
                    onClick={handleDisconnectGmail}
                  >
                    Disconnect Gmail
                  </button>
                </div>
              </>
            )}

            {!gmailSyncStatus.has_credentials && (
              <div className="settings-item">
                <p>Connect your Gmail account to enable automatic email syncing.</p>
                <button 
                  className="connect-button"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard to Connect
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Account Settings */}
        <div className="settings-section">
          <h2>Account</h2>
          <div className="settings-card">
            <div className="settings-item">
              <div className="settings-item-info">
                <strong>Email</strong>
                <p>{userInfo?.email}</p>
              </div>
            </div>
            <div className="settings-item">
              <div className="settings-item-info">
                <strong>Name</strong>
                <p>{userInfo?.first_name} {userInfo?.last_name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="settings-section danger-zone">
          <h2>Danger Zone</h2>
          <div className="settings-card">
            <div className="settings-item">
              <div className="settings-item-info">
                <strong>Sign Out</strong>
                <p>Sign out of your account</p>
              </div>
              <div className="settings-item-action">
                <button 
                  className="logout-button-settings"
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      
      <JobSearchStartDateModal
        isOpen={showStartDateModal}
        onClose={() => setShowStartDateModal(false)}
        onSave={handleSaveStartDate}
        currentDate={profile?.job_search_start_date}
      />
      
      <RescanConfirmModal
        isOpen={showRescanModal}
        onClose={() => setShowRescanModal(false)}
        onConfirm={handleRescanConfirm}
      />
    </div>
    </div>
  );
};

export default Settings;

